import { Scene, ObjectContainer, SceneNode, AnimationClip, } from "@spi/Common/Collada";
import { AnimationFrame } from "./AnimationFrame";
import { mat4 } from "gl-matrix";
import { AnimationDataTexture } from "../../Engine/Texture/AnimationDataTexture";

export class Animation {

    public visualScene: Scene;
    public colladaObject: ObjectContainer;
    public controllerNode: SceneNode;
    public clips: AnimationClip[];
    public clipLength: { [key: string]: number };

    public bindShapeMatrix: mat4;
    public invBindShapeMatrix: mat4;
    public invBindMap: Map<SceneNode, mat4>;
    public joints: SceneNode[];

    public dataTexture: AnimationDataTexture;

    public validAnimation: boolean;

    constructor(visualScene: Scene, colladaObject: ObjectContainer) {
        this.visualScene = visualScene;
        this.colladaObject = colladaObject;
        this.clips = colladaObject.animationClipLibrary;
        this.controllerNode = null;
        this.dataTexture = null;
        this.clipLength = {};
    }

    public preComputeAnimations(controllerNode: SceneNode) {

        this.controllerNode = controllerNode;
        this.buildJoints();

        // Assumes shared parent node for all skeleton nodes
        let root = controllerNode.skeletonNodes[0];

        // TODO: will this work?
        if (this.clips.length === 0) {
            // Build a default animation clip
            // It is assumed that all animations are of equal length in frames

            // this.clips = [{id: 'Idle', animations: this.colladaObject.animationLibrary}];
        }

        for (let clip of this.clips) {
            let frameCount = 0;

            for (let animation of clip.animations) {

                for (let channel of animation.channels) {

                    this.validAnimation = true;

                    let timeInput = channel.sampler.getInput("INPUT");
                    let newMatrixInput = channel.sampler.getInput("OUTPUT");
                    let interpolationInput = channel.sampler.getInput("INTERPOLATION");

                    // TODO: Dirty hack, fix this
                    if (frameCount === 0) {
                        frameCount = newMatrixInput.source.count;
                    }

                    let frames = new Array<AnimationFrame>();
                    let lastFrame: AnimationFrame = null;
                    for (let f = 0; f < frameCount; f++) {
                        let time = timeInput.sample<number[]>(f)[0];
                        let newMatrix = newMatrixInput.sample<mat4>(f);
                        let interpolation = interpolationInput.getValue<string[]>(interpolationInput.sample<number[]>(f)[0])[0];

                        if (interpolation !== 'LINEAR') {  // For now only support LINEAR
                            console.warn(`Unsupported interpolation type found: ${interpolation}`);
                        }

                        let currentFrame = new AnimationFrame(time, newMatrix, interpolation);

                        currentFrame.previousFrame = lastFrame;
                        if (lastFrame) {
                            lastFrame.nextFrame = currentFrame;
                        }

                        lastFrame = currentFrame;

                        frames.push(currentFrame);
                    }

                    channel.frameCount = frameCount;

                    var target = channel.target;
                    var pos = target.indexOf('/');
                    // TODO: Deal with the actual target segment, for now assume all transforms are baked
                    // into a matrix.
                    if (pos !== -1) {
                        target = target.substring(0, pos);
                    }

                    let node = this.visualScene.lookupNode(target);

                    if (node) {
                        channel.targetNode = node;

                        node.animated = true;
                        node.animationFrames[clip.id] = frames;
                        this.clipLength[clip.id] = frameCount;

                    } else {
                        console.warn(`Animation target node ${target} does not exist`);
                    }
                }
            }

            for (let f = 0; f < frameCount; f++) {
                this.computeMatrices(root.parentNode, f, clip.id);
            }
        }

        //	// Don't need these for animation
        //	// TODO: Only for bind pose?
        //	var buildWorldMatrices = function(node) {
        //		if(node.parentNode) {
        //			node.worldMatrix = mat4.multiply(mat4.create(), node.parentNode.worldMatrix, node.jointMatrix);
        //		} else {
        //			node.worldMatrix = mat4.clone(node.jointMatrix);
        //		}
        //		for(var i = 0; i < node.childNodes.length; i++) {
        //			buildWorldMatrices(node.childNodes[i]);
        //		}
        //	};
        //	
        //	buildWorldMatrices(root);
    }

    public getNodeMatrix(node: SceneNode, frameIndex: number, animationState: string) {
        if (node.animated) {
            return node.animationFrames[animationState][frameIndex].getMatrix();
        } else {
            throw new Error(`No node matrix set`);
            //return node.getMatrix();
        }
    }

    public buildJoints() {
        let skin = this.controllerNode.controller.skin;

        this.bindShapeMatrix = skin.bindShapeMatrix;
        this.invBindShapeMatrix = mat4.invert(mat4.create(), skin.bindShapeMatrix);

        this.invBindMap = new Map<SceneNode, mat4>();
        let jointInput = skin.joints.getInput("JOINT");
        let invBindMatInput = skin.joints.getInput("INV_BIND_MATRIX");

        this.joints = new Array<SceneNode>();

        for (let i = 0; i < invBindMatInput.source.count; i++) {
            let invBindMat = invBindMatInput.sample<mat4>(i);
            let jointName = jointInput.getValue<string[]>(jointInput.sample<number[]>(i)[0])[0];

            let joint = this.visualScene.lookupNode(jointName);

            this.invBindMap.set(joint, invBindMat);

            this.joints.push(joint);
        }
    }

    public computeMatrices(root: SceneNode, frameIndex: number, animationState: string) {
        /*
            v += {[(v * BSM) * IBMi * JMi] * JW} 
    
            n: The number of joints that influence vertex v
            BSM: Bind-shape matrix
            IBMi: Inverse bind-pose matrix of joint i
            JMi: Transformation matrix of joint i
            JW: Weight of the influence of joint i on vertex v
    	
    	
            BSM: controller->skin->bind_shape_matrix
            IBMi: controller->skin->joints
            JMi: scene node transforms
            JW: controller->skin->vertex_weights
         */

        var jointMap = new Map<SceneNode, mat4>();
        jointMap.set(root, this.getNodeMatrix(root, frameIndex, animationState));

        let buildAnimationMatrices = (node: SceneNode) => {

            if (node.type === 'JOINT') {
                // Get the parent matrix of transforms so far
                let parentMatrix = jointMap.get(node.parentNode);
                // Get the matrix that defines this stage of the animation
                let animMatrix = this.getNodeMatrix(node, frameIndex, animationState);

                // Multiply the animation matrix with the parent matrix
                let jointMatrix = mat4.multiply(mat4.create(), animMatrix, parentMatrix);
                // Store this matrix for any child nodes
                jointMap.set(node, jointMatrix);

                let invBindMatrix = this.invBindMap.get(node);
                if (!invBindMatrix) {
                    console.error('No inverse bind matrix found for node ', node);
                }

                // Multiply the inverse bind pose with the matrix and store it
                node.animationFrames[animationState][frameIndex].index = frameIndex;
                node.animationFrames[animationState][frameIndex].invBindMatrix = invBindMatrix;
                node.animationFrames[animationState][frameIndex].jointMatrix = jointMatrix;
                node.animationFrames[animationState][frameIndex].worldMatrix = mat4.multiply(mat4.create(), invBindMatrix, jointMatrix);
            }

            node.childNodes.forEach(buildAnimationMatrices);
        };

        buildAnimationMatrices(root);

    }
}
